import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { GalleryImage, GalleryPage } from '@/types/api';

export const galleryKey = ['gallery'] as const;

// Paginated gallery list as an infinite query — the grid lazy-loads page 2+ on scroll
// (roadmap §Iter 8: initial fetch capped at 30, server-side). getNextPageParam reads
// the Laravel paginator meta; undefined when the last page is reached (stops fetching).
export function useGalleryImages() {
  return useInfiniteQuery({
    queryKey: galleryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api.get<GalleryPage>(`/gallery?page=${pageParam}`),
    getNextPageParam: (lastPage) =>
      lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined,
  });
}

// Upload an image + required caption (the inseparable unit). Multipart, since it
// carries a file — same wire format as the Vedlikehold image upload.
export function useUploadGalleryImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ image, caption }: { image: File; caption: string }) => {
      const form = new FormData();
      form.append('image', image);
      form.append('caption', caption);
      return api.postForm<{ data: GalleryImage }>('/gallery', form);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: galleryKey }),
  });
}

// Edit a caption (the only editable field — bytes are immutable). JSON PATCH, no file,
// so no multipart / method-spoofing needed.
export function useEditGalleryCaption() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, caption }: { id: string; caption: string }) =>
      api.patch<{ data: GalleryImage }>(`/gallery/${id}`, { caption }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: galleryKey }),
  });
}

// Soft-delete an image (caption goes with it). own-or-owner+ enforced server-side.
export function useDeleteGalleryImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => api.delete<void>(`/gallery/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: galleryKey }),
  });
}
